import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { ParametresAccessoires, StructureAccessoires, ParametresFigureHumaine, ParametresMobilier, ElementMobilier, ParametresVegetation, ParametresEclairage, TypeMobilier, DimensionsStructure } from '../types';

/**
 * Service 3D pour le module Accessoires
 * Génére les objets 3D des sous-modules: figures, mobilier, végétation
 */
export class Accessoires3DService {
  private scene?: THREE.Scene;

  constructor(scene?: THREE.Scene) {
    this.scene = scene;
  }

  genererAccessoires3D(structure: StructureAccessoires): THREE.Group {
    const root = new THREE.Group();
    root.name = 'AccessoiresRoot';

    console.log('🚀 DEBUG genererAccessoires3D - Structure reçue:', structure);
    console.log('🚀 DEBUG genererAccessoires3D - Paramètres éclairage:', structure.parametres.eclairage);

    // Figures humaines
    if (structure.parametres.figureHumaine?.actif) {
      console.log('👤 Génération figure humaine');
      const figureGroup = this.genererFigureHumaine(structure.parametres.figureHumaine);
      root.add(figureGroup);
    }

    // Éclairage - Sources lumineuses 3D selon cahier des charges
    if (structure.parametres.eclairage?.actif) {
      console.log('💡 Module éclairage ACTIF - Génération des éclairages');
      const eclairageGroup = this.genererEclairage(structure.parametres.eclairage, structure.donneesStructure);
      root.add(eclairageGroup);
      console.log('💡 Groupe éclairage ajouté, enfants:', eclairageGroup.children.length);
    } else {
      console.log('❌ Module éclairage NON ACTIF:', structure.parametres.eclairage);
    }

    // Mobilier
    if (structure.parametres.mobilier?.actif) {
      console.log('🪑 Génération mobilier');
      const mobilierGroup = this.genererMobilier(structure.parametres.mobilier);
      root.add(mobilierGroup);
    }

    // Végétation
    if (structure.parametres.vegetation?.actif) {
      console.log('🌿 Génération végétation');
      const vegetationGroup = this.genererVegetation(structure.parametres.vegetation, structure.donneesStructure.hauteur);
      root.add(vegetationGroup);
    }

    console.log('🚀 DEBUG genererAccessoires3D - Groupe final, enfants:', root.children.length);
    return root;
  }

  mettreAJourCouleurGlobale(root: THREE.Group, couleur: string | number) {
    const color = new THREE.Color(couleur as any);
    root.traverse(obj => {
      const mesh = obj as THREE.Mesh;
      if (mesh.isMesh && mesh.material) {
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        materials.forEach(m => {
          (m as THREE.MeshStandardMaterial).color = color.clone();
          (m as THREE.MeshStandardMaterial).needsUpdate = true;
        });
      }
    });
  }

  private genererFigureHumaine(params: ParametresFigureHumaine): THREE.Group {
    const group = new THREE.Group();
    group.name = 'FigureHumaine';
    group.position.set(params.position.x, params.position.y, params.position.z);
    group.rotation.y = (params.rotation * Math.PI) / 180;
    group.scale.setScalar(params.echelle || 1);
    group.visible = params.visible;

    // Charger le modèle GLB réaliste
    const loader = new GLTFLoader();
    loader.load(
      '/models/realistic_human_figure.glb',
      (gltf) => {
        const model = gltf.scene;
        
        // Configurer le modèle pour un réalisme optimal
        model.scale.setScalar(1.2); // Taille plus réaliste
        model.position.set(0, -1.2, 0);
        
        // Améliorer les matériaux pour plus de réalisme
        model.traverse((child) => {
          if (child instanceof THREE.Mesh && child.material) {
            const materials = Array.isArray(child.material) ? child.material : [child.material];
            materials.forEach((material) => {
              if (material instanceof THREE.MeshStandardMaterial) {
                // Améliorer les propriétés des matériaux
                material.roughness = 0.8;
                material.metalness = 0.1;
                material.envMapIntensity = 1.0;
                
                // Appliquer couleur personnalisée si spécifiée
                if (params.couleur) {
                  material.color.copy(new THREE.Color(params.couleur));
                  material.needsUpdate = true;
                }
              }
            });
          }
        });
        
        // Configurer les ombres pour un rendu réaliste
        model.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            
            // Améliorer la géométrie si nécessaire
            if (child.geometry) {
              child.geometry.computeVertexNormals();
            }
          }
        });
        
        // Adapter pour position assise
         if (params.type === 'personne-assise') {
           model.position.y = -0.8;
           
           // Rotation légère pour une pose plus naturelle
           model.rotation.y = Math.PI * 0.05; // Légère rotation
         }
        
        group.add(model);
        
        console.log('✅ Modèle humain GLB réaliste chargé avec succès');
      },
      (progress) => {
        console.log('📥 Chargement du modèle humain réaliste:', (progress.loaded / progress.total * 100) + '%');
      },
      (error) => {
        console.error('❌ Erreur lors du chargement du modèle humain réaliste:', error);
         // Fallback: créer une figure simple en cas d'erreur
         const fallbackFigure = this.creerFigureFallback(params);
         group.add(fallbackFigure);
      }
    );

    // Éclairage spécifique pour la figure humaine
    if (params.eclairage?.actif) {
      const eclairageGroup = this.creerEclairageFigureHumaine(params.eclairage);
      group.add(eclairageGroup);
    }

    return group;
  }

  private creerChaise(): THREE.Group {
    const chaise = new THREE.Group();
    const matBois = new THREE.MeshStandardMaterial({ color: '#8b4513', roughness: 0.6 });
    
    // Assise
    const assise = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.08, 0.45), matBois);
    assise.position.set(0, 0.4, 0);
    assise.castShadow = true;
    assise.receiveShadow = true;
    chaise.add(assise);
    
    // Dossier
    const dossier = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.6, 0.08), matBois);
    dossier.position.set(0, 0.7, -0.18);
    dossier.castShadow = true;
    chaise.add(dossier);
    
    // Pieds de chaise
    const positions = [[-0.2, -0.15], [0.2, -0.15], [-0.2, 0.15], [0.2, 0.15]];
    positions.forEach(([x, z]) => {
      const pied = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.4, 8), matBois);
      pied.position.set(x, 0.2, z);
      pied.castShadow = true;
      chaise.add(pied);
    });
    
    return chaise;
  }

  private creerFigureFallback(params: ParametresFigureHumaine): THREE.Group {
    const fallback = new THREE.Group();
    const couleur = new THREE.Color(params.couleur || '#fdbcb4');
    const material = new THREE.MeshStandardMaterial({ color: couleur });
    
    // Figure simple de secours
    const tete = new THREE.Mesh(new THREE.SphereGeometry(0.1, 16, 12), material);
    tete.position.y = 1.7;
    tete.castShadow = true;
    fallback.add(tete);
    
    const corps = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.18, 0.8, 12), material);
    corps.position.y = 1.2;
    corps.castShadow = true;
    fallback.add(corps);
    
    const jambeGauche = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.08, 0.6, 8), material);
    jambeGauche.position.set(-0.1, 0.5, 0);
    jambeGauche.castShadow = true;
    fallback.add(jambeGauche);
    
    const jambeDroite = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.08, 0.6, 8), material);
    jambeDroite.position.set(0.1, 0.5, 0);
    jambeDroite.castShadow = true;
    fallback.add(jambeDroite);
    
    console.log('⚠️ Utilisation de la figure de secours');
    return fallback;
  }

  private genererMobilier(params: ParametresMobilier): THREE.Group {
    const group = new THREE.Group();
    group.name = 'Mobilier';

    // Créer éléments paramétrés
    params.elements?.forEach((el: ElementMobilier) => {
      if (!el.visible) return;
      const mesh = this.creerMobilierElement(el, params);
      if (mesh) group.add(mesh);
    });

    return group;
  }

  private creerMobilierElement(el: ElementMobilier, params: ParametresMobilier): THREE.Object3D | null {
    const color = new THREE.Color(el.couleur || '#888');
    let obj: THREE.Object3D | null = null;

    switch (el.type) {
      case TypeMobilier.TABLE: {
        const dims = params.modeles[TypeMobilier.TABLE];
        const plateau = new THREE.Mesh(new THREE.BoxGeometry(dims.longueur, 0.05, dims.largeur), new THREE.MeshStandardMaterial({ color }));
        plateau.position.y = dims.hauteur;
        plateau.castShadow = true;

        const pieds = new THREE.Group();
        const piedGeom = new THREE.CylinderGeometry(0.03, 0.03, dims.hauteur, 12);
        const piedMat = new THREE.MeshStandardMaterial({ color });
        const positions = [
          [dims.longueur/2 - 0.05, dims.hauteur/2, dims.largeur/2 - 0.05],
          [-dims.longueur/2 + 0.05, dims.hauteur/2, dims.largeur/2 - 0.05],
          [dims.longueur/2 - 0.05, dims.hauteur/2, -dims.largeur/2 + 0.05],
          [-dims.longueur/2 + 0.05, dims.hauteur/2, -dims.largeur/2 + 0.05]
        ];
        positions.forEach(p => {
          const pied = new THREE.Mesh(piedGeom, piedMat);
          pied.position.set(p[0], p[1], p[2]);
          pied.castShadow = true;
          pieds.add(pied);
        });

        const table = new THREE.Group();
        table.add(plateau);
        table.add(pieds);
        table.position.set(el.position.x, el.position.y, el.position.z);
        table.rotation.y = (el.rotation * Math.PI) / 180;
        table.scale.setScalar(el.echelle || 1);
        obj = table;
        break;
      }
      case TypeMobilier.CHAISE: {
        const dims = params.modeles[TypeMobilier.CHAISE];
        const assise = new THREE.Mesh(new THREE.BoxGeometry(dims.largeur, 0.04, dims.profondeur), new THREE.MeshStandardMaterial({ color }));
        assise.position.y = dims.hauteurAssise;
        assise.castShadow = true;
        const dossier = new THREE.Mesh(new THREE.BoxGeometry(dims.largeur, dims.hauteur - dims.hauteurAssise, 0.04), new THREE.MeshStandardMaterial({ color }));
        dossier.position.set(0, (dims.hauteur + dims.hauteurAssise)/2, -dims.profondeur/2 + 0.02);
        dossier.castShadow = true;
        const chaise = new THREE.Group();
        chaise.add(assise);
        chaise.add(dossier);
        chaise.position.set(el.position.x, el.position.y, el.position.z);
        chaise.rotation.y = (el.rotation * Math.PI) / 180;
        chaise.scale.setScalar(el.echelle || 1);
        obj = chaise;
        break;
      }
      case TypeMobilier.CANAPE: {
        const dims = params.modeles[TypeMobilier.CANAPE];
        const base = new THREE.Mesh(new THREE.BoxGeometry(dims.largeur, 0.4, dims.profondeur), new THREE.MeshStandardMaterial({ color }));
        base.position.y = 0.2;
        const dossier = new THREE.Mesh(new THREE.BoxGeometry(dims.largeur, 0.5, 0.1), new THREE.MeshStandardMaterial({ color }));
        dossier.position.set(0, 0.65, -dims.profondeur/2 + 0.05);
        const canape = new THREE.Group();
        canape.add(base);
        canape.add(dossier);
        canape.position.set(el.position.x, el.position.y, el.position.z);
        canape.rotation.y = (el.rotation * Math.PI) / 180;
        canape.scale.setScalar(el.echelle || 1);
        obj = canape;
        break;
      }
      case TypeMobilier.BANC: {
        const dims = params.modeles[TypeMobilier.BANC];
        const assise = new THREE.Mesh(new THREE.BoxGeometry(dims.largeur, 0.06, dims.profondeur), new THREE.MeshStandardMaterial({ color }));
        assise.position.y = dims.hauteur;
        const banc = new THREE.Group();
        banc.add(assise);
        banc.position.set(el.position.x, el.position.y, el.position.z);
        banc.rotation.y = (el.rotation * Math.PI) / 180;
        banc.scale.setScalar(el.echelle || 1);
        obj = banc;
        break;
      }
      case TypeMobilier.PARASOL: {
        const dims = params.modeles[TypeMobilier.PARASOL];
        const mat = new THREE.MeshStandardMaterial({ color });
        const matPied = new THREE.MeshStandardMaterial({ color: '#555' });
        const matBaton = new THREE.MeshStandardMaterial({ color: '#666' });
        const pied = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.2, 0.05, 16), matPied);
        pied.position.y = 0.025;
        const baton = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, dims.hauteur, 12), matBaton);
        baton.position.y = dims.hauteur/2 + 0.05;
        const toile = new THREE.Mesh(new THREE.ConeGeometry(dims.diametre/2, dims.diametre/3, 24), mat);
        toile.position.y = dims.hauteur + dims.diametre/6;
        const parasol = new THREE.Group();
        parasol.add(pied);
        parasol.add(baton);
        parasol.add(toile);
        parasol.position.set(el.position.x, el.position.y, el.position.z);
        parasol.rotation.y = (el.rotation * Math.PI) / 180;
        parasol.scale.setScalar(el.echelle || 1);
        obj = parasol;
        break;
      }
      default:
        obj = null;
    }

    return obj;
  }

  private genererVegetation(params: ParametresVegetation, hauteurStructure: number): THREE.Group {
    const group = new THREE.Group();
    group.name = 'Vegetation';

    // Plantes grimpantes simplifiées: tubes le long des montants
    if (params.plantesGrimpantes?.actif) {
      const h = Math.max(0.2, Math.min(1, params.plantesGrimpantes.hauteur)) * hauteurStructure;
      const couleur = new THREE.Color(params.plantesGrimpantes.couleur || '#228B22');
      // Représentation simple: 4 montants aux coins
      const positions = [
        [-1, 0, -1], [1, 0, -1], [-1, 0, 1], [1, 0, 1]
      ];
      positions.forEach(([x, _y, z]) => {
        const geom = new THREE.CylinderGeometry(0.03, 0.03, h, 8);
        const mat = new THREE.MeshStandardMaterial({ color: couleur });
        const liane = new THREE.Mesh(geom, mat);
        liane.position.set(x, h/2, z);
        liane.castShadow = true;
        group.add(liane);
      });
    }

    // Arbustes
    if (params.arbustes?.actif) {
      const mat = new THREE.MeshStandardMaterial({ color: params.arbustes.couleur || '#2E8B57' });
      params.arbustes.positions?.forEach(p => {
        const arbuste = new THREE.Mesh(new THREE.SphereGeometry(params.arbustes.taille || 0.3, 12, 12), mat);
        arbuste.position.set(p.x, p.y + (params.arbustes.taille || 0.3), p.z);
        arbuste.castShadow = true;
        group.add(arbuste);
      });
    }

    // Éclairage spécifique pour la végétation
    if (params.eclairage?.actif) {
      const eclairageGroup = this.creerEclairageVegetation(params.eclairage, hauteurStructure);
      group.add(eclairageGroup);
    }

    return group;
  }

  /**
   * Génère les sources d'éclairage 3D selon le cahier des charges
   * Tâche 5.2: Simulation d'Éclairage Avancée - ÉCLAIRAGE INTÉRIEUR PUISSANT
   */
  private genererEclairage(params: ParametresEclairage | undefined | null, dimensions: DimensionsStructure): THREE.Group {
    const group = new THREE.Group();
    group.name = 'Eclairage3D';

    if (!params) return group;

    // Lampe centrale
    if (params.lampeCentrale) {
      const lampeGroup = this.creerLampeCentrale3D(params.lampeCentrale ?? {}, dimensions);
      // Toujours piloter la visibilité par le flag 'actif' uniquement
      lampeGroup.visible = params.lampeCentrale.actif === true;
      group.add(lampeGroup);
    }

    // Barres LED verticales (toujours créées)
    const barresVerticalesGroup = this.creerBarresLEDVerticales(params.barresLEDVerticales || {}, dimensions);
    barresVerticalesGroup.visible = !!params.barresLEDVerticales?.actif;
    group.add(barresVerticalesGroup);

    // Barres LED horizontales (toujours créées)
    const barresHorizontalesGroup = this.creerBarresLEDHorizontales(params.barresLEDHorizontales || {}, dimensions);
    barresHorizontalesGroup.visible = !!params.barresLEDHorizontales?.actif;
    group.add(barresHorizontalesGroup);

    // Spots directionnels 3D spécifiques (toujours créés)
    const spotsGroup = this.creerSpotsDirectionnels3D(params.spotsDirectionnels || {}, dimensions);
    spotsGroup.visible = !!params.spotsDirectionnels?.actif;
    group.add(spotsGroup);

    // Guirlande LED décorative (toujours créée)
    const guirlandeGroup = this.creerGuirlandeLED3D(params.guirlandeLED || {}, dimensions);
    guirlandeGroup.visible = !!params.guirlandeLED?.actif;
    group.add(guirlandeGroup);

    console.log('🔍 DEBUG genererEclairage - Groupe final, enfants:', group.children.length);
    return group;
  }

  /**
   * Crée un éclairage général puissant pour illuminer tout l'intérieur
   */


  /**
   * Crée une lampe centrale suspendue
   */
  private creerLampeCentrale3D(params: any, dimensions: DimensionsStructure): THREE.Group {
    const group = new THREE.Group();
    group.name = 'LampeCentrale3D';

    const centre = dimensions.centre || { x: 0, y: 0, z: 0 };
    // Position suspendue contrôlée par le slider hauteur (0 = tout en haut du toit, 1 = descend beaucoup plus bas)
    const hauteurRelative = params.hauteur ?? 0.3;
    
    // Utiliser la hauteur du toit si disponible, sinon la hauteur de la structure de base
    const hauteurTotale = dimensions.toit ? 
      (dimensions.hauteur + dimensions.toit.hauteurConvergence) : 
      dimensions.hauteur;
    
    // Permettre à la lampe de descendre jusqu'à mi-hauteur de la structure
    const plageDescente = hauteurTotale * 0.7; // 70% de la hauteur totale
    const positionY = centre.y + hauteurTotale - (hauteurRelative * plageDescente);
    group.position.set(centre.x, positionY, centre.z);

    // Abat-jour avec taille personnalisable
    const taille = params.taille ?? 1.0;
    const abatJour = new THREE.Mesh(
      new THREE.ConeGeometry(0.3 * taille, 0.4 * taille, 16, 1, true),
      new THREE.MeshStandardMaterial({ 
        color: '#f7fafc', 
        transparent: true, 
        opacity: 0.8,
        side: THREE.DoubleSide 
      })
    );
    abatJour.position.y = -0.2 * taille;
    group.add(abatJour);

    // Câble de support (si demandé)
    if (params.fil !== false) {
      const yTopLocal = (centre.y + hauteurTotale) - positionY; // distance jusqu'au toit dans le repère local
      const cableLen = Math.max(0.01, yTopLocal);
      const cable = new THREE.Mesh(
        new THREE.CylinderGeometry(0.01, 0.01, cableLen, 8),
        new THREE.MeshStandardMaterial({ color: '#333333', metalness: 0.3, roughness: 0.8 })
      );
      cable.position.y = cableLen / 2; // du point d’ancrage vers le haut
      cable.castShadow = false;
      cable.receiveShadow = false;
      group.add(cable);
    }

    // Ampoule visible et émissive
    const ampoule = new THREE.Mesh(
      new THREE.SphereGeometry(0.12 * taille, 16, 16),
      new THREE.MeshStandardMaterial({ 
        color: params.couleur || '#ffdd88',
        emissive: new THREE.Color(params.couleur || '#ffdd88'),
        emissiveIntensity: (params.intensite ?? 1) * 0.8, // Émission visible
        transparent: true,
        opacity: 0.9 // Visible
      })
    );
    ampoule.position.y = -0.3 * taille; // Positionnée à l'intérieur de l'abat-jour
    group.add(ampoule);

    // SpotLight dirigé strictement vers le bas (comme un abat-jour)
    const spot = new THREE.SpotLight(
      params.couleur || '#ffdd88',
      (params.intensite ?? 1) * 50 * taille, // Intensité originale
      Math.max(dimensions.largeur, dimensions.longueur) * 2, // Portée adaptée à la taille de la structure
      params.angle ?? Math.PI / 3, // Cône configurable
      0.1 // Pénombre réduite pour un éclairage plus net
    );
    spot.position.set(0, -0.1 * taille, 0);
    // Cible placée au niveau du sol pour éclairer efficacement
    spot.target.position.set(0, centre.y - dimensions.hauteur * 0.8, 0);
    spot.castShadow = true;
    group.add(spot);
    group.add(spot.target);

    // Lumière d'ambiance très faible pour ne pas interférer avec la lampe principale
    const ambiance = new THREE.PointLight(params.couleur || '#ffdd88', (params.intensite ?? 1) * 0.1, 0.3);
    ambiance.position.set(0, -0.3 * taille, 0); // Même position que l'ampoule
    group.add(ambiance);

    // Éclairage de réfraction subtil vers les poutres du haut
    // Simule la lumière qui rebondit sur l'abat-jour et éclaire faiblement les structures du haut
    const refractionLight = new THREE.SpotLight(
      params.couleur || '#ffdd88',
      (params.intensite ?? 1) * 8 * taille, // Intensité originale
      hauteurTotale * 0.8, // Portée limitée pour atteindre les poutres
      Math.PI / 2, // Cône large pour couvrir les poutres
      0.8 // Pénombre importante pour un effet diffus
    );
    refractionLight.position.set(0, -0.2 * taille, 0); // Légèrement au-dessus de l'ampoule
    // Cible dirigée vers le haut pour éclairer les poutres
    refractionLight.target.position.set(0, centre.y + hauteurTotale * 0.9, 0);
    refractionLight.castShadow = true;
    refractionLight.shadow.mapSize.width = 512;
    refractionLight.shadow.mapSize.height = 512;
    group.add(refractionLight);
    group.add(refractionLight.target);

    return group;
  }

  /**
   * Crée des barres LED verticales sur les montants
   */
  private creerBarresLEDVerticales(params: any, dimensions: DimensionsStructure): THREE.Group {
    const group = new THREE.Group();
    group.name = 'BarresLEDVerticales';

    const centre = dimensions.centre || { x: 0, y: 0, z: 0 };
    const positions = [
      { x: -dimensions.largeur/2, z: -dimensions.longueur/2 },
      { x: dimensions.largeur/2, z: -dimensions.longueur/2 },
      { x: dimensions.largeur/2, z: dimensions.longueur/2 },
      { x: -dimensions.largeur/2, z: dimensions.longueur/2 }
    ];

    positions.forEach(pos => {
      // Support métallique
      const support = new THREE.Mesh(
        new THREE.CylinderGeometry(0.02, 0.02, dimensions.hauteur * 0.8, 8),
        new THREE.MeshStandardMaterial({ color: '#4a5568' })
      );
      support.position.set(pos.x, centre.y + dimensions.hauteur * 0.4, pos.z);
      group.add(support);

      // Tube LED lumineux
      const tubeLED = new THREE.Mesh(
        new THREE.CylinderGeometry(0.015, 0.015, dimensions.hauteur * 0.7, 8),
        new THREE.MeshStandardMaterial({
          color: params.couleur || '#ffffff',
          emissive: new THREE.Color(params.couleur || '#ffffff'),
          emissiveIntensity: params.intensite || 0.8,
          transparent: true,
          opacity: 0.9
        })
      );
      tubeLED.position.set(pos.x, centre.y + dimensions.hauteur * 0.4, pos.z);
      group.add(tubeLED);

      // LEDs individuelles
      const nombreLEDs = params.nombreLEDs || 10;
      for (let i = 0; i < nombreLEDs; i++) {
        const y = centre.y + (i / (nombreLEDs - 1)) * dimensions.hauteur * 0.7;
        
        const led = new THREE.Mesh(
          new THREE.SphereGeometry(0.01, 8, 8),
          new THREE.MeshStandardMaterial({
            color: params.couleur || '#ffffff',
            emissive: new THREE.Color(params.couleur || '#ffffff'),
            emissiveIntensity: params.intensite || 0.8
          })
        );
        led.position.set(pos.x, y, pos.z);
        group.add(led);

        // Lumière ponctuelle pour chaque LED (sans ombres pour optimiser)
        const lumiereLED = new THREE.PointLight(
          params.couleur || '#ffffff',
          (params.intensite || 0.8) * 2, // Intensité augmentée
          dimensions.largeur * 0.5
        );
        lumiereLED.position.set(pos.x, y, pos.z);
        lumiereLED.castShadow = true;
        lumiereLED.shadow.mapSize.width = 512;
        lumiereLED.shadow.mapSize.height = 512;
        group.add(lumiereLED);
      }
    });

    return group;
  }

  /**
   * Crée des barres LED horizontales sur le périmètre
   */
  private creerBarresLEDHorizontales(params: any, dimensions: DimensionsStructure): THREE.Group {
    const group = new THREE.Group();
    group.name = 'BarresLEDHorizontales';

    const centre = dimensions.centre || { x: 0, y: 0, z: 0 };
    const hauteurBarre = centre.y + dimensions.hauteur * 0.8;

    // Barres sur les 4 côtés
    const cotes = [
      { debut: { x: -dimensions.largeur/2, z: -dimensions.longueur/2 }, fin: { x: dimensions.largeur/2, z: -dimensions.longueur/2 } },
      { debut: { x: dimensions.largeur/2, z: -dimensions.longueur/2 }, fin: { x: dimensions.largeur/2, z: dimensions.longueur/2 } },
      { debut: { x: dimensions.largeur/2, z: dimensions.longueur/2 }, fin: { x: -dimensions.largeur/2, z: dimensions.longueur/2 } },
      { debut: { x: -dimensions.largeur/2, z: dimensions.longueur/2 }, fin: { x: -dimensions.largeur/2, z: -dimensions.longueur/2 } }
    ];

    cotes.forEach(cote => {
      const longueur = Math.sqrt(
        Math.pow(cote.fin.x - cote.debut.x, 2) + 
        Math.pow(cote.fin.z - cote.debut.z, 2)
      );
      const centreX = (cote.debut.x + cote.fin.x) / 2;
      const centreZ = (cote.debut.z + cote.fin.z) / 2;
      const angle = Math.atan2(cote.fin.z - cote.debut.z, cote.fin.x - cote.debut.x);

      // Barre LED principale
      const barre = new THREE.Mesh(
        new THREE.CylinderGeometry(0.02, 0.02, longueur, 8),
        new THREE.MeshStandardMaterial({
          color: params.couleur || '#ffffff',
          emissive: new THREE.Color(params.couleur || '#ffffff'),
          emissiveIntensity: params.intensite || 0.8,
          transparent: true,
          opacity: 0.9
        })
      );
      barre.position.set(centreX, hauteurBarre, centreZ);
      barre.rotation.z = Math.PI / 2;
      barre.rotation.y = angle;
      group.add(barre);

      // LEDs individuelles
      const nombreLEDs = params.nombreLEDs || 20;
      for (let i = 0; i < nombreLEDs; i++) {
        const t = i / (nombreLEDs - 1);
        const x = cote.debut.x + (cote.fin.x - cote.debut.x) * t;
        const z = cote.debut.z + (cote.fin.z - cote.debut.z) * t;
        
        const led = new THREE.Mesh(
          new THREE.SphereGeometry(0.01, 8, 8),
          new THREE.MeshStandardMaterial({
            color: params.couleur || '#ffffff',
            emissive: new THREE.Color(params.couleur || '#ffffff'),
            emissiveIntensity: params.intensite || 0.8
          })
        );
        led.position.set(x, hauteurBarre, z);
        group.add(led);

        // Lumière ponctuelle pour chaque LED (sans ombres pour optimiser)
         const lumiereLED = new THREE.PointLight(
            params.couleur || '#ffffff',
            (params.intensite || 0.8) * 2,
            dimensions.largeur * 0.3
          );
         lumiereLED.position.set(x, hauteurBarre, z);
         lumiereLED.castShadow = true;
         lumiereLED.shadow.mapSize.width = 512;
         lumiereLED.shadow.mapSize.height = 512;
         group.add(lumiereLED);
      }
    });

    return group;
  }

  /**
   * Crée des spots directionnels 3D spécifiques
   */
  private creerSpotsDirectionnels3D(params: any, dimensions: DimensionsStructure): THREE.Group {
    const group = new THREE.Group();
    group.name = 'SpotsDirectionnels3D';

    const centre = dimensions.centre || { x: 0, y: 0, z: 0 };
    const nombre = params.nombre || 4;
    const rayon = Math.min(dimensions.largeur, dimensions.longueur) * 0.3;

    for (let i = 0; i < nombre; i++) {
      const angle = (i / nombre) * Math.PI * 2;
      const x = centre.x + Math.cos(angle) * rayon;
      const z = centre.z + Math.sin(angle) * rayon;
      const y = centre.y + dimensions.hauteur * 0.9;

      // Boîtier du projecteur
      const boitier = new THREE.Mesh(
        new THREE.CylinderGeometry(0.08, 0.1, 0.15, 12),
        new THREE.MeshStandardMaterial({ color: '#2d3748' })
      );
      boitier.position.set(x, y, z);
      boitier.rotation.x = -Math.PI / 4;
      group.add(boitier);

      // Lentille émissive
      const lentille = new THREE.Mesh(
        new THREE.CircleGeometry(0.06, 16),
        new THREE.MeshStandardMaterial({
          color: params.couleur || '#ffffff',
          emissive: new THREE.Color(params.couleur || '#ffffff'),
          emissiveIntensity: params.intensite || 1.2,
          transparent: true,
          opacity: 0.8
        })
      );
      lentille.position.set(x, y - 0.075, z);
      lentille.rotation.x = -Math.PI / 2;
      group.add(lentille);

      // Spot light
      const spot = new THREE.SpotLight(
        params.couleur || '#ffffff',
        (params.intensite || 1.2) * 5,
        dimensions.largeur * 2,
        params.angle || 0.5,
        0.2
      );
      spot.position.set(x, y, z);
      spot.target.position.set(centre.x, centre.y, centre.z);
      spot.castShadow = true;
      spot.shadow.mapSize.width = 512;
      spot.shadow.mapSize.height = 512;
      group.add(spot);
      group.add(spot.target);
    }

    return group;
  }

  /**
   * Crée une guirlande LED décorative 3D spécifique
   */
  private creerGuirlandeLED3D(params: any, dimensions: DimensionsStructure): THREE.Group {
    const group = new THREE.Group();
    group.name = 'GuirlandeLED3D';

    const centre = dimensions.centre || { x: 0, y: 0, z: 0 };
    const hauteurGuirlande = centre.y + dimensions.hauteur * 0.85;
    const nombreAmpoules = params.nombreAmpoules || 15;
    const rayonGuirlande = Math.min(dimensions.largeur, dimensions.longueur) * 0.4;

    // Câble principal de la guirlande
    const pointsCable = [];
    for (let i = 0; i <= nombreAmpoules; i++) {
      const angle = (i / nombreAmpoules) * Math.PI * 2;
      const x = centre.x + Math.cos(angle) * rayonGuirlande;
      const z = centre.z + Math.sin(angle) * rayonGuirlande;
      const y = hauteurGuirlande - Math.sin(i * 0.5) * 0.1; // Effet de suspension
      pointsCable.push(new THREE.Vector3(x, y, z));
    }

    const geometrieCable = new THREE.BufferGeometry().setFromPoints(pointsCable);
    const cable = new THREE.Line(
      geometrieCable,
      new THREE.LineBasicMaterial({ color: '#2d3748', linewidth: 3 })
    );
    group.add(cable);

    // Ampoules de guirlande
    for (let i = 0; i < nombreAmpoules; i++) {
      const angle = (i / nombreAmpoules) * Math.PI * 2;
      const x = centre.x + Math.cos(angle) * rayonGuirlande;
      const z = centre.z + Math.sin(angle) * rayonGuirlande;
      const y = hauteurGuirlande - Math.sin(i * 0.5) * 0.1;

      // Ampoule de guirlande
      const ampoule = new THREE.Mesh(
        new THREE.SphereGeometry(0.05, 12, 12),
        new THREE.MeshStandardMaterial({
          color: params.couleur || '#ffdd88',
          emissive: new THREE.Color(params.couleur || '#ffdd88'),
          emissiveIntensity: params.intensite || 0.6,
          transparent: true,
          opacity: 0.9
        })
      );
      ampoule.position.set(x, y, z);
      group.add(ampoule);

      // Douille de l'ampoule
      const douille = new THREE.Mesh(
        new THREE.CylinderGeometry(0.02, 0.025, 0.03, 8),
        new THREE.MeshStandardMaterial({ color: '#2d3748' })
      );
      douille.position.set(x, y + 0.04, z);
      group.add(douille);

      // Lumière ponctuelle seulement pour quelques ampoules (optimisation)
      let lumiereAmpoule: THREE.PointLight | null = null;
      if (i % 4 === 0) { // Une lumière toutes les 4 ampoules
        lumiereAmpoule = new THREE.PointLight(
          params.couleur || '#ffdd88',
          (params.intensite || 0.6) * 8, // Intensité augmentée pour compenser
          dimensions.largeur * 0.8 // Portée augmentée
        );
        lumiereAmpoule.position.set(x, y, z);
        lumiereAmpoule.castShadow = true;
        lumiereAmpoule.shadow.mapSize.width = 512;
        lumiereAmpoule.shadow.mapSize.height = 512;
        group.add(lumiereAmpoule);
      }

      // Effet scintillant optionnel
      if (params.effet === 'scintillant' && lumiereAmpoule) {
        setTimeout(() => {
          setInterval(() => {
            const intensiteAleatoire = (params.intensite || 0.6) * (0.3 + Math.random() * 0.7);
            if (lumiereAmpoule) {
              lumiereAmpoule.intensity = intensiteAleatoire * 2;
            }
            ampoule.material.emissiveIntensity = intensiteAleatoire;
          }, 100 + Math.random() * 200);
        }, i * 50); // Décalage pour éviter la synchronisation
      }
    }

    return group;
  }

  /**
   * Crée l'éclairage spécifique pour une figure humaine
   */
  private creerEclairageFigureHumaine(params: any): THREE.Group {
    const group = new THREE.Group();
    group.name = 'EclairageFigureHumaine';

    if (params.type === 'spot') {
      // SpotLight directionnel depuis la figure
      const spotLight = new THREE.SpotLight(
        params.couleur || '#ffffff',
        params.intensite || 1.0,
        params.portee || 3.0,
        Math.PI / 6, // Angle de 30°
        0.2 // Pénombre
      );
      spotLight.position.set(0, 1.6, 0); // Au niveau de la tête
      spotLight.target.position.set(0, 0, 1); // Éclaire vers l'avant
      spotLight.castShadow = true;
      spotLight.shadow.mapSize.width = 512;
      spotLight.shadow.mapSize.height = 512;
      group.add(spotLight);
      group.add(spotLight.target);
    } else {
      // PointLight omnidirectionnel
      const pointLight = new THREE.PointLight(
        params.couleur || '#ffffff',
        params.intensite || 0.8,
        params.portee || 2.5
      );
      pointLight.position.set(0, 1.6, 0); // Au niveau de la tête
      pointLight.castShadow = true;
      pointLight.shadow.mapSize.width = 512;
      pointLight.shadow.mapSize.height = 512;
      group.add(pointLight);
    }

    return group;
  }

  /**
   * Crée l'éclairage spécifique pour la végétation
   */
  private creerEclairageVegetation(params: any, hauteurStructure: number): THREE.Group {
    const group = new THREE.Group();
    group.name = 'EclairageVegetation';

    if (params.type === 'diffus') {
      // Éclairage diffus pour illuminer uniformément la végétation
      const ambientLight = new THREE.AmbientLight(
        params.couleur || '#90EE90',
        params.intensite || 0.3
      );
      group.add(ambientLight);

      // Ajout de quelques points lumineux pour accentuer
      const positions = [
        [-0.8, hauteurStructure * 0.3, -0.8],
        [0.8, hauteurStructure * 0.3, -0.8],
        [-0.8, hauteurStructure * 0.3, 0.8],
        [0.8, hauteurStructure * 0.3, 0.8]
      ];

      positions.forEach(([x, y, z]) => {
        const pointLight = new THREE.PointLight(
          params.couleur || '#90EE90',
          params.intensite || 0.4,
          params.portee || 2.0
        );
        pointLight.position.set(x, y, z);
        pointLight.castShadow = true;
        pointLight.shadow.mapSize.width = 512;
        pointLight.shadow.mapSize.height = 512;
        group.add(pointLight);
      });
    } else {
      // Éclairage d'accent - spots directionnels vers les cibles spécifiques
      if (params.cibles.includes('plantes')) {
        // Spots pour les plantes grimpantes
        const positions = [[-1, hauteurStructure * 0.8, -1], [1, hauteurStructure * 0.8, 1]];
        positions.forEach(([x, y, z]) => {
          const spotLight = new THREE.SpotLight(
            params.couleur || '#32CD32',
            params.intensite || 0.6,
            params.portee || 3.0,
            Math.PI / 4,
            0.3
          );
          spotLight.position.set(x, y, z);
          spotLight.target.position.set(x, 0, z);
          spotLight.castShadow = true;
          spotLight.shadow.mapSize.width = 512;
          spotLight.shadow.mapSize.height = 512;
          group.add(spotLight);
          group.add(spotLight.target);
        });
      }

      if (params.cibles.includes('arbustes')) {
        // Spots pour les arbustes
        const spotLight = new THREE.SpotLight(
          params.couleur || '#228B22',
          params.intensite || 0.5,
          params.portee || 2.5,
          Math.PI / 3,
          0.2
        );
        spotLight.position.set(0, hauteurStructure * 0.6, 0);
        spotLight.target.position.set(0, 0, 0);
        spotLight.castShadow = true;
        spotLight.shadow.mapSize.width = 512;
        spotLight.shadow.mapSize.height = 512;
        group.add(spotLight);
        group.add(spotLight.target);
      }

      if (params.cibles.includes('decoration')) {
        // Éclairage décoratif général
        const decorLight = new THREE.PointLight(
          params.couleur || '#FFD700',
          params.intensite || 0.3,
          params.portee || 1.5
        );
        decorLight.position.set(0, hauteurStructure * 0.4, 0);
        decorLight.castShadow = true;
        decorLight.shadow.mapSize.width = 512;
        decorLight.shadow.mapSize.height = 512;
        group.add(decorLight);
      }
    }

    return group;
  }
}